"use client"

import { usePosts } from "@letterhead/core/react"
import { useEffect, useState } from "react"
import { Post } from "@letterhead/core"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import Link from "next/link"
import dayjs from "dayjs"
import Image from "next/image"
import { useTranslations, useLocale } from "next-intl"

function BlogList() {
  const t = useTranslations('blogPage')
  const posts = usePosts()
  const [blogPosts, setBlogPosts] = useState<Post[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>("All")
  const [categories, setCategories] = useState<string[]>(["All"])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const locale = useLocale()

  useEffect(() => {
    async function fetchPosts() {
      try {
        const result = await posts.list(1, 50)
        let fetchedPosts = result.posts;
        
        if (locale !== 'en' && fetchedPosts.length > 0) {
            try {
                // Collect all translatable strings to minimize API requests
                const textsToTranslate: string[] = [];
                fetchedPosts.forEach((post) => {
                    textsToTranslate.push(post.title || '');
                    textsToTranslate.push(post.excerpt || '');
                });

                const res = await fetch('/api/translate', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ texts: textsToTranslate, targetLang: locale })
                });

                if (res.ok) {
                    const data = await res.json();
                    if (data.translated && Array.isArray(data.translated)) {
                         fetchedPosts = fetchedPosts.map((post, idx) => {
                             const titleIdx = idx * 2;
                             const excerptIdx = titleIdx + 1;
                             return {
                                 ...post,
                                 title: data.translated[titleIdx] || post.title,
                                 excerpt: data.translated[excerptIdx] || post.excerpt
                             };
                         });
                    }
                }
            } catch (translationErr) {
                console.error("Translation feature suppressed:", translationErr);
            }
        }

        // Extract unique categories/tags
        const allTags = new Set<string>();
        fetchedPosts.forEach(post => {
            const tags = post.metadata?.seo_tags || post.tags || [];
            tags.forEach(tag => allTags.add(tag));
        });
        setCategories(["All", ...Array.from(allTags).sort()]);

        setBlogPosts(fetchedPosts)
      } catch (err: any) {
        let msg = err.message || t("failedToLoad")
        if (msg === "Failed to fetch") msg = t("failedToFetch")
        setError(msg)
      } finally {
        setIsLoading(false)
      }
    }
    fetchPosts()
  }, [posts])

  if (isLoading) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-muted rounded w-3/4 mb-2" />
              <div className="h-4 bg-muted rounded w-1/2" />
            </CardHeader>
            <CardContent>
              <div className="h-20 bg-muted rounded w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-2">{t("failedToLoad")}</p>
        <p className="text-sm text-muted-foreground">{error}</p>
      </div>
    )
  }

  if (blogPosts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t("noPosts")}</p>
      </div>
    )
  }

  const filteredPosts = selectedCategory === "All" 
    ? blogPosts 
    : blogPosts.filter(post => {
        const tags = post.metadata?.seo_tags || post.tags || [];
        return tags.includes(selectedCategory);
      });

  return (
    <div>
      <div>
        <h2 className='py-6 sm:py-10 uppercase tracking-widest text-sm text-gray-400'>{t("latestArticles")}</h2>
      </div>

      {categories.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm transition-colors ${
                selectedCategory === category
                  ? "bg-teal-600 text-white"
                  : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
              }`}
            >
              {category === "All" ? t("allCategories") || "All" : category}
            </button>
          ))}
        </div>
      )}

      <ul className="grid gap-6 sm:gap-10 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {filteredPosts.map((post, index) => <li className='flex py-6 sm:py-10' key={index}>
          <div className='w-full'>
            {post.header_image && (
              <div className="relative rounded-sm w-full h-[200px] sm:h-[250px] overflow-hidden">
                <Image src={post.header_image} fill alt={post.title} className="object-cover" unoptimized={true} />
              </div>
            )}
            <span className='flex items-center gap-x-3 sm:gap-x-5 text-sm py-3'>
              <p className='text-xs text-gray-300 uppercase tracking-widest'>{dayjs(post.created_at).format('MMMM DD, YYYY')}</p>
              {(() => {
                const displayTags = post.metadata?.seo_tags || post.tags || [];
                // Only show first 2 tags
                const tagsToShow = displayTags.slice(0, 2);

                return tagsToShow.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {tagsToShow.map((tag, idx) => (
                      <Link key={idx} href={`/blog/tags/${tag}`} className='text-xs text-[#08C4DE] hover:underline'>
                        {tag}
                      </Link>
                    ))}
                  </div>
                ) : null;
              })()}
            </span>
            <Link className='inline-block font-semibold hover:text-[#08C4DE] hover:underline text-lg sm:text-xl pb-5 break-words' href={`/blog/${post.slug}`}>{post.title}</Link>
            <span>
              <p className='text-sm sm:text-base pb-5 text-gray-400 line-clamp-3'>{post.excerpt}</p>
            </span>
            {/*<span className='flex items-center'>
              <Image src={post.authorImage} width={30} height={30} alt={post.authorImage} className='rounded-full' />
              <p className='font-semibold text-[16px] pl-3'>{post.author}</p>
            </span>*/}
          </div>
          <div>
            {/* <Image objectFit='cover' src={post.image} alt={post.title} width={450} height={300} className='rounded-lg' /> */}
          </div>
        </li>)}
      </ul>
    </div>
  )
}

export default function BlogClient() {
  const t = useTranslations('blogPage')
  return (
    <div className="container mx-auto py-8 sm:py-12 px-4 sm:px-6 md:px-8">
      <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">
        <div className="text-center space-y-3 sm:space-y-4">
          <h1 className="text-3xl sm:text-4xl font-bold">{t("title")}</h1>
          <p className="text-base sm:text-lg text-muted-foreground">{t("subtitle")}</p>
        </div>
        <BlogList />
      </div>
    </div>
  )
}
